import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@codes/ui-kit/components/ui/sheet';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

import type { SecretCredentialBody, SecretCredentialView } from '@codes/access-keys';
import { useCreateAccessKey, useUpdateAccessKey } from '@codes/access-keys';

interface AccessKeyFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
    keyData: SecretCredentialView | null;
    onSuccess: (credential?: { accessKey: string; secretKey: string }) => void;
}

type FormErrors = {
    userName?: string;
    accessKey?: string;
    secretKey?: string;
};

export const AccessKeyFormDialog = ({ open, onOpenChange, mode, keyData, onSuccess }: AccessKeyFormDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const createMutation = useCreateAccessKey();
    const updateMutation = useUpdateAccessKey();

    // Controlled state
    const [userName, setUserName] = useState('');
    const [accessKey, setAccessKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [sessionToken, setSessionToken] = useState('');
    const [expiredAt, setExpiredAt] = useState('');
    const [arn, setArn] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});

    useEffect(() => {
        if (open) {
            if (mode === 'edit' && keyData) {
                setUserName(keyData.userName || '');
                setAccessKey(keyData.accessKey);
                setSecretKey(keyData.secretKey);
                setSessionToken(keyData.sessionToken || '');
                setExpiredAt(keyData.expiredAt ? new Date(keyData.expiredAt).toISOString().split('T')[0] : '');
                setArn(keyData.arn || '');
            } else if (mode === 'create') {
                setUserName('');
                setAccessKey('');
                setSecretKey('');
                setSessionToken('');
                setExpiredAt('');
                setArn('');
            }
            setErrors({});
        }
    }, [open, mode, keyData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!accessKey.trim()) {
            newErrors.accessKey = 'Access Key is required';
        }
        if (!secretKey.trim()) {
            newErrors.secretKey = 'Secret Key is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const body: SecretCredentialBody = {
            userName: userName.trim() || undefined,
            accessKey: accessKey.trim(),
            secretKey: secretKey.trim(),
            sessionToken: sessionToken.trim() || undefined,
            expiredAt: expiredAt ? new Date(expiredAt).getTime() : undefined,
            arn: arn.trim() || undefined,
        };

        try {
            if (mode === 'create') {
                const result = await createMutation.mutateAsync({ body });
                toast({
                    title: t('accessKeys.createSuccess'),
                });
                onOpenChange(false);
                onSuccess({ accessKey: result.accessKey, secretKey: result.secretKey });
            } else if (mode === 'edit' && keyData?.id) {
                await updateMutation.mutateAsync({ id: keyData.id, body });
                toast({
                    title: t('accessKeys.updateSuccess'),
                });
                onOpenChange(false);
                onSuccess();
            }
        } catch (error) {
            toast({
                title: t('accessKeys.error'),
                description: error instanceof Error ? error.message : 'Unknown error',
                variant: 'destructive',
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[540px]">
                <SheetHeader aria-describedby={undefined}>
                    <SheetTitle>{mode === 'create' ? t('accessKeys.createNew') : t('accessKeys.form.edit')}</SheetTitle>
                </SheetHeader>

                <div className="flex flex-col gap-y-6 py-6">
                    <div className="text-gray-600 dark:text-gray-400">
                        {mode === 'create'
                            ? 'Create a new secret credential with access key and secret key.'
                            : 'Update the secret credential information.'}
                    </div>

                    <form onSubmit={onSubmit} className="flex flex-col gap-y-6">
                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="userName">User Name (Optional)</Label>
                            <Input
                                id="userName"
                                value={userName}
                                onChange={e => setUserName(e.target.value)}
                                placeholder="Enter user name"
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                Optional identifier for this credential
                            </p>
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="accessKey">Access Key *</Label>
                            <Input
                                id="accessKey"
                                value={accessKey}
                                onChange={e => setAccessKey(e.target.value)}
                                placeholder="Enter access key"
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            {errors.accessKey && <p className="text-sm text-destructive mt-1">{errors.accessKey}</p>}
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="secretKey">Secret Key *</Label>
                            <Textarea
                                id="secretKey"
                                value={secretKey}
                                onChange={e => setSecretKey(e.target.value)}
                                placeholder="Enter secret key"
                                rows={3}
                                disabled={isLoading}
                            />
                            {errors.secretKey && <p className="text-sm text-destructive mt-1">{errors.secretKey}</p>}
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="sessionToken">Session Token (Optional)</Label>
                            <Input
                                id="sessionToken"
                                value={sessionToken}
                                onChange={e => setSessionToken(e.target.value)}
                                placeholder="Enter session token"
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            <p className="text-xs text-gray-600 dark:text-gray-400">For temporary credentials (STS)</p>
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="expiredAt">Expiration Date (Optional)</Label>
                            <Input
                                id="expiredAt"
                                type="date"
                                value={expiredAt}
                                onChange={e => setExpiredAt(e.target.value)}
                                disabled={isLoading}
                            />
                            <p className="text-xs text-gray-600 dark:text-gray-400">Leave empty for no expiration</p>
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="arn">ARN (Optional)</Label>
                            <Input
                                id="arn"
                                value={arn}
                                onChange={e => setArn(e.target.value)}
                                placeholder="arn:aws:iam::123456789012:user/..."
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            <p className="text-xs text-gray-600 dark:text-gray-400">AWS Resource Name</p>
                        </div>

                        <SheetFooter className="pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('accessKeys.deleteDialog.cancel')}
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {mode === 'create' ? t('accessKeys.generate') : t('accessKeys.update')}
                            </Button>
                        </SheetFooter>
                    </form>
                </div>
            </SheetContent>
        </Sheet>
    );
};
